import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import {
  CreditCard, Bank, WhatsappLogo, CheckCircle, ArrowRight,
  Upload, ClockCountdown, Copy, MusicNote, ShieldCheck,
  VinylRecord, Disc, Key, Sliders, Sparkle, Warning, CircleNotch
} from "@phosphor-icons/react";

const POLL_MS = 8000;

const PACKAGES = [
  {
    key: "single",
    name: "Single",
    price: 49,
    credits: 1,
    tag: "Empieza aquí",
    icon: MusicNote,
    accent: "#FFB800",
    desc: "1 canción personalizada con portada individual",
    features: ["Letra única con IA", "Portada de álbum personalizada", "Audio HD descargable"],
  },
  {
    key: "ep",
    name: "Extended Play",
    price: 120,
    credits: 3,
    tag: "Más popular · ahorra $27",
    icon: VinylRecord,
    accent: "#B4F34A",
    desc: "3 canciones + 1 portada de EP con tracklist",
    features: ["3 canciones personalizadas", "Portada de EP con los 3 títulos", "Ideal para dedicatorias múltiples"],
    highlighted: true,
  },
  {
    key: "album",
    name: "Album",
    price: 249,
    credits: 7,
    tag: "Pro · ahorra $94",
    icon: Disc,
    accent: "#FF4B00",
    desc: "7 canciones + 1 portada de Álbum con tracklist",
    features: ["7 canciones personalizadas", "Portada de Álbum con tracklist completo", "Para un proyecto musical completo"],
  },
];

export default function Payment() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [method, setMethod] = useState(""); // "stripe" | "bbva"
  const [loading, setLoading] = useState(false);
  const [bbva, setBbva] = useState(null); // {transaction_id, reference, whatsapp_link, ...}
  const [bbvaStatus, setBbvaStatus] = useState(null); // {song_credits, pending_bbva}
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [ocrResult, setOcrResult] = useState(null); // {passed, reason, failures, detected_amount, detected_reference, confidence}
  const [selectedPkg, setSelectedPkg] = useState("ep");
  const [pinValue, setPinValue] = useState("");
  const [redeemingPin, setRedeemingPin] = useState(false);
  const pollRef = useRef(null);

  const redeemPin = async () => {
    if (!/^\d{6}$/.test(pinValue)) return toast.error("El PIN debe ser de 6 dígitos");
    setRedeemingPin(true);
    try {
      await api.post("/payments/redeem-pin", { pin: pinValue });
      await refreshUser();
      toast.success("¡PIN válido! Ya puedes crear tu canción 🎉");
      nav("/create");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PIN no válido");
    } finally { setRedeemingPin(false); }
  };

  // Redirect when credit lands
  useEffect(() => {
    if (user?.song_credits > 0) {
      // user has credit → can go straight to creation
    }
  }, [user]);

  // Poll credit status while user is on BBVA flow
  useEffect(() => {
    if (!bbva) return;
    const tick = async () => {
      try {
        const { data } = await api.get("/payments/credits/status");
        setBbvaStatus(data);
        if (data.song_credits > 0) {
          await refreshUser();
          toast.success("¡Pago confirmado! Ya puedes crear tu canción 🎉");
          nav("/create");
        }
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.warn("[Payment] poll failed:", e?.response?.status || e?.message);
        }
      }
    };
    tick();
    pollRef.current = setInterval(tick, POLL_MS);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bbva, nav, refreshUser]);

  const startStripe = async () => {
    setLoading(true); setMethod("stripe");
    try {
      const { data } = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
        method: "stripe",
        purpose: "credit",
        package: selectedPkg,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error iniciando pago Stripe");
      setLoading(false); setMethod("");
    }
  };

  const startBbva = async () => {
    setLoading(true); setMethod("bbva");
    try {
      const { data } = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
        method: "bbva",
        purpose: "credit",
        package: selectedPkg,
      });
      setBbva(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error iniciando transferencia");
      setMethod("");
    } finally { setLoading(false); }
  };

  const copyAll = () => {
    if (!bbva) return;
    const text = `Banco: ${bbva.bank}\nBeneficiario: ${bbva.account_name}\nCuenta/CLABE: ${bbva.clabe_or_account}\nMonto: $${bbva.amount} MXN\nReferencia: ${bbva.reference}`;
    navigator.clipboard.writeText(text);
    toast.success("Datos copiados");
  };

  const copy = (val) => { navigator.clipboard.writeText(val); toast.success("Copiado"); };

  const uploadReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Máximo 8MB");
    setUploadingReceipt(true);
    setOcrResult({ analyzing: true });
    const fd = new FormData();
    fd.append("file", file);
    fd.append("transaction_id", bbva.transaction_id);
    try {
      const { data } = await api.post("/payments/bbva/upload-receipt", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBbva({ ...bbva, has_receipt: true });
      setOcrResult({ ...data.ocr, auto_confirmed: data.auto_confirmed });
      if (data.auto_confirmed) {
        toast.success("¡Pago verificado automáticamente! 🎉");
        // The polling effect already redirects when song_credits > 0
        await refreshUser();
      } else {
        toast.info("Comprobante recibido — un administrador lo revisará pronto");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error subiendo comprobante");
      setOcrResult(null);
    } finally { setUploadingReceipt(false); }
  };

  // If user already has credit, show "Ready to create" CTA
  const hasCredit = user?.song_credits > 0;

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="payment-page">

        {hasCredit && !bbva && (
          <div className="rounded-2xl bg-[#B4F34A]/10 border border-[#B4F34A]/30 p-5 mb-6 flex items-center gap-4" data-testid="has-credit-banner">
            <div className="w-12 h-12 rounded-full bg-[#B4F34A] flex items-center justify-center shrink-0">
              <CheckCircle weight="fill" size={24} className="text-black" />
            </div>
            <div className="flex-1">
              <p className="font-display font-bold text-lg">Tienes {user.song_credits} crédito{user.song_credits > 1 ? 's' : ''}</p>
              <p className="text-sm text-white/70">Ya puedes crear tu canción.</p>
            </div>
            <Link to="/create" className="px-4 py-2 rounded-full bg-[#FFB800] text-black font-semibold text-sm flex items-center gap-1.5" data-testid="go-create">
              Crear <ArrowRight weight="bold" size={14} />
            </Link>
          </div>
        )}

        <p className="text-sm uppercase tracking-widest text-[#FFB800] mb-2">Elige tu paquete</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-6">¿Cuántas canciones quieres?</h1>

        {/* Package selector */}
        {!bbva && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8" data-testid="package-grid">
            {PACKAGES.map(p => {
              const on = selectedPkg === p.key;
              const Icon = p.icon;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setSelectedPkg(p.key)}
                  data-testid={`pkg-${p.key}`}
                  className={`text-left rounded-2xl p-5 border-2 transition-all active:scale-[0.99] relative ${
                    on
                      ? 'border-[#FFB800] bg-[#FFB800]/5'
                      : 'border-white/10 bg-[#121212] hover:border-white/30'
                  }`}
                >
                  {p.highlighted && !on && (
                    <span className="absolute -top-2 right-3 px-2 py-0.5 rounded-full bg-[#B4F34A] text-black text-[10px] font-bold tracking-widest uppercase">{p.tag.split('·')[0].trim()}</span>
                  )}
                  <div className="flex items-center justify-between mb-3">
                    <Icon size={28} weight="duotone" style={{ color: p.accent }} />
                    {on && <CheckCircle weight="fill" size={20} className="text-[#FFB800]" />}
                  </div>
                  <p className="font-display font-black text-xl">{p.name}</p>
                  <p className="text-xs text-white/50 mt-0.5 mb-3">{p.credits} canci{p.credits === 1 ? 'ón' : 'ones'}</p>
                  <p className="font-display text-3xl font-black mb-1">${p.price}<span className="text-sm text-white/50 font-normal"> MXN</span></p>
                  <p className="text-[10px] uppercase tracking-widest text-white/40 mb-3">{p.tag}</p>
                  <ul className="space-y-1.5">
                    {p.features.map((f) => (
                      <li key={f} className="text-xs text-white/70 flex items-start gap-1.5">
                        <CheckCircle weight="fill" size={12} className="text-[#B4F34A] mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>
        )}

        {/* STUDIO PRO promo card — for professional musicians */}
        <Link
          to="/studio/info"
          className="block mb-8 relative overflow-hidden rounded-3xl border border-[#FFB800]/30 bg-gradient-to-br from-[#1a1405] via-[#0a0a0a] to-[#050505] p-6 sm:p-7 hover:border-[#FFB800]/60 transition-all group"
          data-testid="studio-pro-card"
        >
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#FFB800]/15 blur-3xl pointer-events-none" />
          <div className="relative flex items-start gap-4 flex-wrap sm:flex-nowrap">
            <div className="w-14 h-14 rounded-2xl bg-[#FFB800] flex items-center justify-center shrink-0">
              <Sliders weight="fill" size={28} className="text-black" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/40 mb-2">
                <span className="text-[10px] uppercase tracking-widest text-[#FFB800] font-bold">Nuevo · Studio Pro</span>
              </div>
              <h3 className="font-display text-2xl sm:text-3xl font-black tracking-tight">
                ¿Eres músico profesional?
              </h3>
              <p className="text-sm text-white/70 mt-1.5 leading-relaxed">
                Si manejas <span className="text-white">progresiones de acordes, tonalidad, modos, BPM y estructura</span> —
                este modo es para ti. Cobra <span className="text-[#FFB800] font-bold">por tiempo de uso ($8 MXN/min)</span>,
                no por canción. Escribe tu propia letra y <span className="text-[#B4F34A]">solo consume IA cuando tú lo decidas</span>.
              </p>
              <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs text-white/65">
                <li>· Selector de progresión I-V-vi-IV / Am-F-C-G</li>
                <li>· Tonalidad y modos griegos completos</li>
                <li>· BPM exacto + métricas 3/4, 5/4, 7/8</li>
                <li>· Bloques Intro · Verso · Coro · Puente · Outro</li>
                <li>· Escribe tu propia letra (sin costo IA)</li>
                <li>· Asistente IA opcional cuando lo necesites</li>
              </ul>
              <div className="mt-4 flex gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 text-xs text-[#FFB800] font-bold group-hover:gap-2 transition-all">
                  Conocer Studio Pro <ArrowRight size={14} weight="bold" />
                </span>
                <span className="text-xs text-white/40">· Desde $120 MXN (15 min)</span>
              </div>
            </div>
          </div>
        </Link>

        <p className="text-sm uppercase tracking-widest text-[#FFB800] mb-2">Método de pago</p>
        <h2 className="font-display text-2xl font-bold mb-6">Activa tu {PACKAGES.find(p => p.key === selectedPkg)?.name}</h2>

        {/* No BBVA flow active: show method selection */}
        {!bbva && (
          <>
            <div className="space-y-3 mb-6" data-testid="payment-methods">
              {/* STRIPE option */}
              <button
                onClick={startStripe}
                disabled={loading}
                data-testid="method-stripe"
                className="w-full p-5 rounded-2xl border-2 border-white/10 hover:border-[#FFB800] bg-[#121212] text-left transition-all active:scale-[0.99] disabled:opacity-50"
              >
                <div className="flex items-center gap-4 mb-2">
                  <div className="w-11 h-11 rounded-xl bg-[#FFB800]/10 flex items-center justify-center">
                    <CreditCard size={22} weight="duotone" className="text-[#FFB800]" />
                  </div>
                  <div className="flex-1">
                    <p className="font-display font-bold text-lg">Tarjeta · Stripe</p>
                    <p className="text-xs text-white/50">Débito o crédito · Activación instantánea</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#B4F34A]/15 text-[#B4F34A]">Rápido</span>
                </div>
                <p className="text-xs text-white/40 pl-15 ml-15">
                  Tu crédito se activa automáticamente en cuanto Stripe confirma el pago.
                </p>
              </button>

              {/* BBVA option */}
              <button
                onClick={startBbva}
                disabled={loading}
                data-testid="method-bbva"
                className="w-full p-5 rounded-2xl border-2 border-white/10 hover:border-[#FFB800] bg-[#121212] text-left transition-all active:scale-[0.99] disabled:opacity-50"
              >
                <div className="flex items-center gap-4 mb-2">
                  <div className="w-11 h-11 rounded-xl bg-[#B4F34A]/10 flex items-center justify-center">
                    <Bank size={22} weight="duotone" className="text-[#B4F34A]" />
                  </div>
                  <div className="flex-1">
                    <p className="font-display font-bold text-lg">Transferencia BBVA</p>
                    <p className="text-xs text-white/50">SPEI · Activación automática con IA</p>
                  </div>
                  <span className="px-2 py-0.5 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/40 text-[10px] font-bold text-[#FFB800] uppercase tracking-widest">Nuevo</span>
                </div>
                <p className="text-xs text-white/40">
                  Transfiere desde tu app de banco, sube la foto del comprobante y nuestra IA verifica el pago al instante. Crea tu canción en segundos.
                </p>
              </button>

              {/* ACCESS PIN option (cash payments) */}
              <div
                className="w-full p-5 rounded-2xl border-2 border-dashed border-[#B4F34A]/40 bg-[#121212]"
                data-testid="method-pin"
              >
                <div className="flex items-center gap-4 mb-2">
                  <div className="w-11 h-11 rounded-xl bg-[#B4F34A]/10 flex items-center justify-center">
                    <Key size={22} weight="duotone" className="text-[#B4F34A]" />
                  </div>
                  <div className="flex-1">
                    <p className="font-display font-bold text-lg">¿Tienes PIN de acceso?</p>
                    <p className="text-xs text-white/50">Pagaste en efectivo · Digítalo aquí y crea tu canción al instante</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={pinValue}
                    onChange={(e) => setPinValue(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="PIN de 6 dígitos"
                    className="flex-1 px-4 py-3 rounded-xl bg-[#0a0a0a] border border-white/10 focus:border-[#B4F34A] focus:outline-none transition-colors text-white placeholder-white/30 tracking-[0.3em] font-mono text-center"
                    data-testid="pin-input"
                  />
                  <button
                    onClick={redeemPin}
                    disabled={redeemingPin || pinValue.length !== 6}
                    className="px-5 py-3 rounded-xl bg-[#B4F34A] hover:bg-[#A0E03A] disabled:opacity-30 text-black font-semibold transition-all active:scale-95"
                    data-testid="pin-redeem-btn"
                  >
                    {redeemingPin ? "…" : "Canjear"}
                  </button>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/5 bg-[#0a0a0a] p-4 flex items-start gap-3">
              <ShieldCheck size={18} className="text-[#B4F34A] mt-0.5 shrink-0" />
              <p className="text-xs text-white/50 leading-relaxed">
                Tu pago compra <span className="text-white">1 crédito</span> = 1 canción completa. No hay cargos recurrentes. Si tienes problemas, escríbenos al WhatsApp <span className="text-[#B4F34A] font-mono">+52 33 4280 2102</span>.
              </p>
            </div>
          </>
        )}

        {/* BBVA active flow */}
        {bbva && (
          <div className="space-y-5" data-testid="bbva-flow">
            {/* Status bar */}
            <div className={`rounded-2xl p-4 border flex items-center gap-3 ${bbvaStatus?.pending_bbva?.status === 'pending_review' ? 'bg-[#FFB800]/10 border-[#FFB800]/30' : 'bg-white/5 border-white/10'}`}>
              <ClockCountdown size={22} className={bbvaStatus?.pending_bbva?.status === 'pending_review' ? 'text-[#FFB800]' : 'text-white/50'} />
              <div className="flex-1">
                <p className="font-display font-bold text-sm">
                  {bbvaStatus?.pending_bbva?.status === 'pending_review'
                    ? 'Revisando tu pago…'
                    : 'Esperando tu transferencia'}
                </p>
                <p className="text-xs text-white/50">Esta pantalla se actualiza sola. No la cierres.</p>
              </div>
            </div>

            {/* Step 1: Transfer details */}
            <div className="rounded-2xl bg-[#121212] border border-white/10 p-5">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-6 h-6 rounded-full bg-[#FFB800] text-black text-xs font-bold flex items-center justify-center">1</span>
                <p className="font-display font-bold">Transfiere $49 MXN</p>
              </div>
              <div className="space-y-2 text-sm" data-testid="bbva-details">
                <Row label="Banco" value={bbva.bank} />
                <Row label="Beneficiario" value={bbva.account_name} />
                <Row label="Cuenta / CLABE" value={bbva.clabe_or_account} onCopy={() => copy(bbva.clabe_or_account)} />
                <Row label="Monto" value={`$${bbva.amount} MXN`} highlight />
                <Row label="Referencia" value={bbva.reference} onCopy={() => copy(bbva.reference)} />
              </div>
              <button
                onClick={copyAll}
                className="mt-3 w-full py-2.5 rounded-full bg-white/5 hover:bg-white/10 text-sm flex items-center justify-center gap-2"
                data-testid="copy-all-bank"
              >
                <Copy size={14} /> Copiar todos los datos
              </button>
            </div>

            {/* Step 2: Report via WhatsApp */}
            <div className="rounded-2xl bg-[#121212] border border-white/10 p-5">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-6 h-6 rounded-full bg-[#FFB800] text-black text-xs font-bold flex items-center justify-center">2</span>
                <p className="font-display font-bold">Reporta tu pago por WhatsApp</p>
              </div>
              <p className="text-sm text-white/60 mb-4">
                Toca el botón y envíanos la <span className="text-white font-medium">foto de tu comprobante</span> al WhatsApp del administrador. Es lo más rápido.
              </p>
              <a
                href={bbva.whatsapp_link}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-4 rounded-full bg-[#25D366] hover:bg-[#1FB855] text-white font-semibold flex items-center justify-center gap-2 transition-all active:scale-95"
                data-testid="whatsapp-report"
              >
                <WhatsappLogo size={22} weight="fill" /> Reportar al WhatsApp +52 33 4280 2102
              </a>

              <p className="text-xs text-white/40 mt-4 mb-2 uppercase tracking-widest">o bien, sube el comprobante aquí</p>
              {bbva.has_receipt ? (
                <OcrFeedback ocr={ocrResult} />
              ) : (
                <>
                  <label
                    htmlFor="bbva-receipt-input"
                    className="block border-2 border-dashed border-white/15 rounded-xl p-5 text-center cursor-pointer hover:border-[#FFB800] hover:bg-white/5 transition-all"
                    data-testid="receipt-dropzone"
                  >
                    <Upload size={22} className="mx-auto text-white/40 mb-2" />
                    <p className="text-sm font-medium">{uploadingReceipt ? "Subiendo y validando con IA…" : "Toca para subir foto del comprobante"}</p>
                    <p className="text-[11px] text-white/40 mt-1">Verificamos automáticamente monto y referencia</p>
                  </label>
                  <input
                    id="bbva-receipt-input"
                    type="file"
                    accept="image/*"
                    onChange={uploadReceipt}
                    className="hidden"
                    data-testid="receipt-input"
                  />
                </>
              )}
            </div>

            {/* Step 3: Wait */}
            <div className="rounded-2xl bg-[#121212] border border-white/10 p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-6 h-6 rounded-full bg-[#FFB800] text-black text-xs font-bold flex items-center justify-center">3</span>
                <p className="font-display font-bold">Validación automática con IA</p>
              </div>
              <p className="text-sm text-white/60 leading-relaxed">
                Cuando subes tu comprobante, nuestra IA verifica el <span className="text-white">monto</span> y la <span className="text-white">referencia</span> al instante. Si todo coincide, se activa de inmediato tu crédito y podrás <span className="text-[#FFB800] font-medium">crear tu canción en segundos</span>.
              </p>
              <p className="text-xs text-white/40 mt-2 flex items-center gap-1">
                <ShieldCheck size={14} weight="fill" className="text-[#B4F34A]" /> Si la IA detecta dudas, un humano revisa en menos de 10 min.
              </p>
            </div>

            <button
              onClick={() => setBbva(null)}
              className="w-full py-3 rounded-full bg-white/5 hover:bg-white/10 text-sm text-white/70"
              data-testid="back-to-methods"
            >
              Cambiar método de pago
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, onCopy, highlight }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
      <span className="text-white/50 text-xs uppercase tracking-widest">{label}</span>
      <button
        onClick={onCopy}
        className={`text-right font-mono ${onCopy ? 'cursor-pointer hover:text-[#FFB800]' : ''} ${highlight ? 'text-[#B4F34A] font-bold' : ''}`}
      >
        {value}{onCopy ? ' ⧉' : ''}
      </button>
    </div>
  );
}

function OcrFeedback({ ocr }) {
  if (!ocr) {
    return (
      <div className="rounded-xl bg-white/5 border border-white/10 p-3 flex items-center gap-2" data-testid="receipt-saved-banner">
        <CheckCircle weight="fill" size={18} className="text-white/40" />
        <p className="text-sm text-white/60">Comprobante recibido</p>
      </div>
    );
  }
  if (ocr.analyzing) {
    return (
      <div className="rounded-xl bg-[#FFB800]/10 border border-[#FFB800]/30 p-4 flex items-center gap-3" data-testid="ocr-analyzing">
        <CircleNotch weight="bold" size={20} className="text-[#FFB800] animate-spin" />
        <div>
          <p className="text-sm font-semibold text-[#FFB800]">Validando con IA…</p>
          <p className="text-xs text-white/50">Leyendo monto y referencia del comprobante</p>
        </div>
      </div>
    );
  }
  if (ocr.auto_confirmed || ocr.passed) {
    return (
      <div className="rounded-xl bg-[#B4F34A]/10 border border-[#B4F34A]/30 p-4" data-testid="ocr-success">
        <div className="flex items-center gap-3 mb-2">
          <Sparkle weight="fill" size={20} className="text-[#B4F34A]" />
          <p className="text-sm font-bold text-[#B4F34A]">Pago verificado automáticamente</p>
        </div>
        <p className="text-xs text-white/60 leading-relaxed">
          La IA confirmó el monto y la referencia. Tu crédito ya está activo — te llevamos a crear tu canción 🎵
        </p>
      </div>
    );
  }
  // Failed verification — pending manual review
  return (
    <div className="rounded-xl bg-[#FFB800]/10 border border-[#FFB800]/30 p-4" data-testid="ocr-pending">
      <div className="flex items-center gap-3 mb-2">
        <Warning weight="fill" size={20} className="text-[#FFB800]" />
        <p className="text-sm font-bold text-[#FFB800]">Revisión manual en proceso</p>
      </div>
      <p className="text-xs text-white/70 leading-relaxed mb-2">
        La IA no pudo confirmar tu pago automáticamente. Un administrador lo revisará en menos de 10 minutos y se activará tu crédito.
      </p>
      {ocr.failures && ocr.failures.length > 0 && (
        <ul className="text-[11px] text-white/40 mt-2 space-y-0.5">
          {ocr.failures.map((f, i) => <li key={i}>· {f}</li>)}
        </ul>
      )}
    </div>
  );
}
